import React from 'react';
import {
  Box,
  Chip,
  Stack,
  Typography,
  Paper,
  useTheme,
  alpha,
  Divider,
  Fade,
  useMediaQuery,
  Collapse,
  IconButton,
} from '@mui/material';
import { Work, Code, Psychology, FilterList, Terminal, ExpandMore } from '@mui/icons-material';

function FilterBar({
  categories,
  selectedCategory,
  onCategoryChange,
  allTags,
  selectedTags,
  onTagsChange,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showTags, setShowTags] = React.useState(!isMobile);

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Enterprise':
        return <Work sx={{ fontSize: 18 }} />;
      case 'Independent':
        return <Code sx={{ fontSize: 18 }} />;
        case 'Prototype':
        return <Psychology sx={{ fontSize: 18 }} />;
      default:
        return <FilterList sx={{ fontSize: 18 }} />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'All':
        return '#00ff41';
      case 'Enterprise':
        return '#00ffff';
      case 'Independent':
        return '#ff6b35';
      case 'Prototype':
        return '#ffab00';
      default:
        return '#666666';
    }
  };

  const handleTagToggle = (tag) => {
    setSelectedTags(prev => 
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const setSelectedTags = onTagsChange;

  return (
    <Paper
      elevation={0}
      className="terminal-window"
      sx={{
        p: 0,
        background: `
          linear-gradient(145deg, #0a0a0a, #111111),
          radial-gradient(circle at top left, ${alpha('#00ffff', 0.05)} 0%, transparent 50%)
        `,
        border: `1px solid ${alpha('#00ffff', 0.3)}`,
        borderRadius: 1,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 30,
          background: `linear-gradient(90deg, ${alpha('#00ffff', 0.2)}, ${alpha('#00ffff', 0.1)})`,
          borderBottom: `1px solid ${alpha('#00ffff', 0.3)}`,
        },
      }}
    >
      {/* Terminal Header */}
      <Box
        sx={{
          height: 30,
          display: 'flex',
          alignItems: 'center',
          px: 2,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Stack direction="row" spacing={0.5} sx={{ mr: 2 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ff5f56' }} />
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ffbd2e' }} />
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#27ca3f' }} />
        </Stack>
        <Typography
          variant="caption"
          sx={{
            color: alpha('#00ffff', 0.8),
            fontFamily: '"Fira Code", monospace',
            fontSize: { xs: '0.65rem', sm: '0.7rem' },
          }}
        >
          filter_config.sh
        </Typography>
      </Box>

      {/* Terminal Content */}
      <Box sx={{ p: { xs: 2, sm: 3 }, pt: 2 }}>
        <Stack spacing={{ xs: 2, sm: 3 }}>
          <Box>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: '#00ffff',
                fontFamily: '"Fira Code", monospace',
                fontSize: { xs: '0.8rem', sm: '0.9rem' },
              }}
            >
              <Terminal sx={{ fontSize: 18 }} />
              Categories
            </Typography>
            <Stack 
              direction="row" 
              spacing={1} 
              flexWrap="wrap" 
              useFlexGap
              sx={{ gap: { xs: 0.5, sm: 1 } }}
            >
              {categories.map((category) => {
                const isSelected = selectedCategory === category;
                const categoryColor = getCategoryColor(category);
                
                return (
                  <Chip
                    key={category}
                    label={category}
                    icon={getCategoryIcon(category)}
                    variant={isSelected ? 'filled' : 'outlined'}
                    onClick={() => onCategoryChange(category)}
                    size={isMobile ? 'small' : 'medium'}
                    sx={{
                      fontWeight: 500,
                      fontFamily: '"Fira Code", monospace',
                      fontSize: { xs: '0.7rem', sm: '0.8rem' },
                      background: isSelected 
                        ? `linear-gradient(145deg, ${categoryColor}, ${alpha(categoryColor, 0.8)})`
                        : `linear-gradient(145deg, ${alpha(categoryColor, 0.1)}, transparent)`,
                      border: `1px solid ${alpha(categoryColor, isSelected ? 1 : 0.3)}`,
                      color: isSelected ? (category === 'All' ? '#000' : '#000') : categoryColor,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: isSelected
                          ? `linear-gradient(145deg, ${alpha(categoryColor, 0.8)}, ${alpha(categoryColor, 0.6)})`
                          : `linear-gradient(145deg, ${alpha(categoryColor, 0.2)}, ${alpha(categoryColor, 0.1)})`,
                        border: `1px solid ${categoryColor}`,
                        boxShadow: `0 0 15px ${alpha(categoryColor, 0.4)}`,
                        transform: isMobile ? 'none' : 'translateY(-1px)',
                      },
                      '& .MuiChip-icon': {
                        fontSize: { xs: 16, sm: 18 },
                        color: 'inherit',
                      },
                    }}
                  />
                );
              })}
            </Stack>
          </Box>

          <Divider 
            sx={{ 
              borderColor: alpha('#00ffff', 0.2),
              '&::before, &::after': {
                borderColor: alpha('#00ffff', 0.2),
              },
            }} 
          />

          <Box>
            <Box
              sx={{
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: '#00ffff',
                  fontFamily: '"Fira Code", monospace',
                  fontSize: { xs: '0.8rem', sm: '0.9rem' },
                }}
              >
                <Terminal sx={{ fontSize: 18 }} />
                Tags
              </Typography>
              
              {isMobile && (
                <IconButton
                  size="small"
                  onClick={() => setShowTags(!showTags)}
                  sx={{
                    color: '#00ffff',
                    transform: showTags ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s ease',
                  }}
                >
                  <ExpandMore />
                </IconButton>
              )}
              
              {selectedTags.length > 0 && !isMobile && (
                <Chip
                  label={`${selectedTags.length} selected`}
                  size="small"
                  variant="outlined"
                  onDelete={() => setSelectedTags([])}
                  sx={{ 
                    fontSize: '0.7rem',
                    fontFamily: '"Fira Code", monospace',
                    background: `linear-gradient(145deg, ${alpha('#ffab00', 0.1)}, transparent)`,
                    border: `1px solid ${alpha('#ffab00', 0.3)}`,
                    color: '#ffab00',
                    '&:hover': {
                      background: `linear-gradient(145deg, ${alpha('#ffab00', 0.2)}, ${alpha('#ffab00', 0.1)})`,
                      border: `1px solid #ffab00`,
                    },
                    '& .MuiChip-deleteIcon': {
                      color: '#ffab00',
                      '&:hover': {
                        color: '#ff6b35',
                      },
                    },
                  }}
                />
              )}
            </Box>

            {selectedTags.length > 0 && isMobile && (
              <Box sx={{ mb: 2 }}>
                <Chip
                  label={`${selectedTags.length} filters selected`}
                  size="small"
                  variant="outlined"
                  onDelete={() => setSelectedTags([])}
                  sx={{ 
                    fontSize: '0.7rem',
                    fontFamily: '"Fira Code", monospace',
                    background: `linear-gradient(145deg, ${alpha('#ffab00', 0.1)}, transparent)`,
                    border: `1px solid ${alpha('#ffab00', 0.3)}`,
                    color: '#ffab00',
                    '& .MuiChip-deleteIcon': {
                      color: '#ffab00',
                    },
                  }}
                />
              </Box>
            )}

            <Collapse in={showTags} timeout={300}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {allTags.map((tag) => {
                  const isSelected = selectedTags.includes(tag);
                  
                  return (
                    <Chip
                      key={tag}
                      label={tag}
                      variant={isSelected ? 'filled' : 'outlined'}
                      onClick={() => handleTagToggle(tag)}
                      size="small"
                      sx={{
                        fontSize: { xs: '0.7rem', sm: '0.75rem' },
                        fontFamily: '"Fira Code", monospace',
                        background: isSelected 
                          ? `linear-gradient(145deg, #ff6b35, ${alpha('#ff6b35', 0.8)})`
                          : `linear-gradient(145deg, ${alpha('#ff6b35', 0.1)}, transparent)`,
                        border: `1px solid ${alpha('#ff6b35', isSelected ? 1 : 0.3)}`,
                        color: isSelected ? '#000' : '#ff6b35',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          background: isSelected
                            ? `linear-gradient(145deg, ${alpha('#ff6b35', 0.8)}, ${alpha('#ff6b35', 0.6)})`
                            : `linear-gradient(145deg, ${alpha('#ff6b35', 0.2)}, ${alpha('#ff6b35', 0.1)})`,
                          border: `1px solid #ff6b35`,
                          boxShadow: `0 0 10px ${alpha('#ff6b35', 0.3)}`,
                          transform: isMobile ? 'none' : 'scale(1.05)',
                        },
                      }}
                    />
                  );
                })}
              </Stack>
            </Collapse>
          </Box>
        </Stack>
      </Box>
    </Paper>
  );
}

export default FilterBar; 